import React, {useEffect, useState} from 'react';
import styled, {keyframes} from 'styled-components';
import {useTranslation} from 'react-i18next';
import {httpsCallable} from 'firebase/functions';
import {functions} from '../firebase';
import OpeningHours from './OpeningHours';
import {FaMapMarkerAlt} from 'react-icons/fa';
import {Helmet} from "react-helmet";

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const spin = keyframes`
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
`;

const ContactContainer = styled.div`
    max-width: 800px;
    margin: 40px auto;
    padding: 20px;
    animation: ${fadeIn} 0.5s ease-out forwards;
`;

const ContactTitle = styled.h2`
    color: ${props => props.theme.colors.primary};
    font-size: 2.5rem;
    text-align: center;
    margin-bottom: 20px;
`;

const ContactInfo = styled.div`
    max-width: 800px;
    background-color: ${props => props.theme.colors.background}cc;
    padding: 20px;
    border-radius: 10px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
`;

const ContactForm = styled.form`
    max-width: 800px;
    display: flex;
    flex-direction: column;
    gap: 15px;
    background-color: ${props => props.theme.colors.background}cc;
    padding: 20px;
    border-radius: 10px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
`;

const Input = styled.input`
    padding: 10px;
    font-size: 1rem;
    border: 1px solid ${props => props.theme.colors.secondary};
    border-radius: 5px;
    outline: none;
    transition: border-color 0.3s ease;

    &:focus {
        border-color: ${props => props.theme.colors.primary};
    }
`;

const TextArea = styled.textarea`
    padding: 10px;
    font-size: 1rem;
    border: 1px solid ${props => props.theme.colors.secondary};
    border-radius: 5px;
    resize: vertical;
    min-height: 100px;
    outline: none;
    transition: border-color 0.3s ease;

    &:focus {
        border-color: ${props => props.theme.colors.primary};
    }
`;

const SubmitButton = styled.button`
    padding: 10px 20px;
    font-size: 1rem;
    color: ${props => props.theme.colors.background};
    background-color: ${props => props.theme.colors.primary};
    border: none;
    border-radius: 5px;
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.3s ease;
    opacity: ${props => (props.disabled ? 0.6 : 1)};
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
        background-color: ${props => props.theme.colors.secondary};
        transform: scale(1.01);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
`;

const Spinner = styled.div`
    width: 20px;
    height: 20px;
    border: 3px solid ${props => props.theme.colors.background}50;
    border-top: 3px solid ${props => props.theme.colors.background};
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
    margin-left: 10px;
`;

const FeedbackMessage = styled.div`
    padding: 10px;
    border-radius: 5px;
    text-align: center;
    font-size: 1rem;
    color: ${props => (props.isError ? '#D8000C' : '#4CAF50')};
    background-color: ${props => (props.isError ? '#FFBABA' : '#DFF2BF')};
    animation: ${fadeIn} 0.5s ease-out forwards;
`;

const ContactInfoText = styled.p`
    display: flex;
    flex-direction: column;
    color: ${props => props.theme.colors.text};
    font-size: 1.1rem;
    margin: 10px 0;
`;

const ReachUsButton = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.background};
    padding: 10px 20px;
    border-radius: 5px;
    font-size: 1rem;
    font-weight: 500;
    text-decoration: none;
    transition: all 0.3s ease;
    animation: ${fadeIn} 0.5s ease-out forwards;
    margin-top: 10px;

    &:hover {
        background-color: ${props => props.theme.colors.secondary};
        transform: scale(1.05);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    @media (max-width: 768px) {
        font-size: 0.9rem;
        padding: 8px 16px;
    }
`;

const Contact = ({rentUser}) => {
    const {t, i18n} = useTranslation();
    const [formData, setFormData] = useState({name: '', email: '', message: ''});
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

    // Rileva il cambio di dimensione dello schermo
    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth > 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const validateForm = () => {
        if (!formData.name.trim()) {
            setErrorMessage(t('nameRequired', {defaultValue: 'Il nome è obbligatorio'}));
            return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setErrorMessage(t('invalidEmail', {defaultValue: 'Inserisci un’email valida'}));
            return false;
        }
        if (!formData.message.trim()) {
            setErrorMessage(t('messageRequired', {defaultValue: 'Il messaggio è obbligatorio'}));
            return false;
        }
        return true;
    };

    const handleChange = (e) => {
        setFormData({...formData, [e.target.name]: e.target.value});
        setErrorMessage('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        setLoading(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const rentUserWebsiteInfoRequestInfoCommand = httpsCallable(functions, 'rentUserWebsiteInfoRequestInfoCommand');
            const response = await rentUserWebsiteInfoRequestInfoCommand({
                rentId: rentUser?.rentId,
                name: formData.name,
                email: formData.email,
                message: formData.message,
            });

            if (response.data.success) {
                setSuccessMessage(t('contactSuccess'));
                setFormData({name: '', email: '', message: ''});
            } else {
                setErrorMessage(t('contactError'));
            }
        } catch (error) {
            console.error('Errore nell\'invio del form:', error);
            setErrorMessage(t('contactError'));
        } finally {
            setLoading(false);
        }
    };

    const currentLang = (i18n.resolvedLanguage || i18n.language || 'it').split('-')[0].toLowerCase();
    const cityStr = typeof rentUser?.city === 'object'
        ? (rentUser?.city[currentLang] || rentUser?.city.it || rentUser?.city.en || '')
        : (rentUser?.city || '');
    const streetStr = typeof rentUser?.street === 'object'
        ? (rentUser?.street[currentLang] || rentUser?.street.it || rentUser?.street.en || '')
        : (rentUser?.street || '');

    const address = cityStr && streetStr ? `${cityStr}, ${streetStr}` : cityStr || streetStr || '';
    const encodedAddress = encodeURIComponent(address);
    const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;

    return (
        <ContactContainer>
            <Helmet>
                <script type="application/ld+json">{`
        {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "${rentUser?.name || 'Bike Rental'}",
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "${streetStr}",
                "addressLocality": "${cityStr}",
                "addressCountry": "IT"
            },
            "telephone": "${rentUser?.contacts?.phoneNumber || ''}",
            "email": "${rentUser?.contacts?.email || ''}",
            "url": "https://locemsolutions.com"
        }
    `}</script>
            </Helmet>
            <ContactTitle>{t('contactUs')}</ContactTitle>
            <ContactForm onSubmit={handleSubmit}>
                <Input
                    type="text"
                    name="name"
                    placeholder={t('namePlaceholder')}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={loading}
                />
                <Input
                    type="email"
                    name="email"
                    placeholder={t('emailPlaceholder')}
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={loading}
                />
                <TextArea
                    name="message"
                    placeholder={t('messagePlaceholder')}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    disabled={loading}
                />
                <SubmitButton type="submit" disabled={loading}>
                    {loading ? (
                        <>
                            {t('sending')}
                            <Spinner/>
                        </>
                    ) : (
                        t('submit')
                    )}
                </SubmitButton>
                {successMessage && <FeedbackMessage>{successMessage}</FeedbackMessage>}
                {errorMessage && <FeedbackMessage isError>{errorMessage}</FeedbackMessage>}
            </ContactForm>
            <ContactInfo>
                <ContactInfoText>
                    <strong>{t('email')}</strong>
                    {rentUser?.contacts?.email || 'N/D'}
                </ContactInfoText>
                <ContactInfoText>
                    <strong>{t('phone')}</strong>
                    {rentUser?.contacts?.phoneNumber || 'N/D'}
                </ContactInfoText>
                <ContactInfoText>
                    <strong>{t('address')}</strong>
                    {address || 'N/D'}
                    {address && (
                        <ReachUsButton
                            href={mapUrl}
                            target={isDesktop ? '_blank' : '_self'}
                            rel="noopener noreferrer"
                            aria-label={t('reachUsAriaLabel', {address})}
                        >
                            <FaMapMarkerAlt style={{marginRight: '8px'}}/> {t('reachUs')}
                        </ReachUsButton>
                    )}
                </ContactInfoText>
            </ContactInfo>
            <OpeningHours rentUser={rentUser}/>
        </ContactContainer>
    );
};

export default Contact;
